import React, { useState, useEffect } from "react";
import {
  Modal,
  Checkbox,
  FormControlLabel,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { useAuth } from "../../AuthContext";
import "../../style/KanbanBoard.css";
import "../../style/KanbanResponsive.css"; // Add this line to import responsive styles

const KanbanBoardTwo = ({
  selectedTags,
  setSelectedTags,
  columns,
  setColumns,
  customTags,
  setCustomTags,
  onSearch,
  searchResults,
  onResultClick,
}) => {
  const { userId } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_SOURCE;

  const defaultCategories = [
    { id: "saved_jobs", name: "Saved Jobs" },
    { id: "applied", name: "Applied" },
    { id: "interview", name: "Interview" },
    { id: "offered", name: "Offered" },
  ];

  // Create an array of default category IDs
  const defaultCategoryIds = defaultCategories.map((cat) => cat.id);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [newTag, setNewTag] = useState("");
  const [categoryLimitError, setCategoryLimitError] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [noResultsAlert, setNoResultsAlert] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  // State for inline editing of custom tags
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingTagName, setEditingTagName] = useState("");

  // Reusable function to fetch custom tags from backend
  const fetchTags = () => {
    if (userId) {
      fetch(
        `${API_BASE_URL}/backend/api/JobsPage/fetchTags.php?userId=${userId}`
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") {
            setCustomTags(data.tags);
          } else {
            console.error("Error fetching tags:", data.message);
          }
        })
        .catch((err) => console.error("Error fetching tags:", err));
    }
  };

  // Fetch custom tags on mount and when userId changes
  useEffect(() => {
    if (userId) {
      fetchTags();
    }
  }, [userId]);

  // Add new tag and update state
  const addCustomTag = () => {
    if (newTag.trim() && customTags.length < 4) {
      fetch(`${API_BASE_URL}/backend/api/JobsPage/addTag.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, tag: newTag.trim() }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") {
            const newTagObject = { id: data.tagId, name: newTag.trim() };
            setCustomTags([...customTags, newTagObject]);
            setSelectedTags([...selectedTags, newTagObject.id]);
            setNewTag("");
            setColumns((prevColumns) => [...prevColumns, newTagObject]);
          } else {
            //alert("Error adding tag: " + data.message);
          }
        })
        .catch((err) => console.error("Error adding tag:", err));
    }
  };

  // Remove a tag and update state
  const removeCustomTag = () => {
    if (!tagToDelete) return;

    fetch(`${API_BASE_URL}/backend/api/JobsPage/deleteTag.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, tagId: tagToDelete }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setCustomTags(customTags.filter((tag) => tag.id !== tagToDelete));
          setSelectedTags(selectedTags.filter((tag) => tag !== tagToDelete));
          setColumns((prevColumns) =>
            prevColumns.filter((col) => col.id !== tagToDelete)
          );
          setIsDeleteModalOpen(false);
          setTagToDelete(null);
          fetchTags(); // Re-fetch tags after deletion
        } else {
          alert("Error deleting tag: " + data.message);
        }
      })
      .catch((err) => console.error("Error deleting tag:", err));
  };

  const handleTagToggle = (tagId) => {
    setSelectedTags((prev) => {
      const isSelected = prev.includes(tagId);
      let updated;

      if (isSelected) {
        if (prev.length === 1) {
          // Prevent unchecking the last selected category
          setCategoryLimitError("Need to select 1 category at least");
          return prev;
        }
        updated = prev.filter((id) => id !== tagId);
      } else {
        if (prev.length >= 4) {
          setCategoryLimitError("Can select only up to 4 categories");
          return prev;
        }
        updated = [...prev, tagId];
      }

      // Clear error if within valid range
      if (updated.length >= 1 && updated.length <= 4) {
        setCategoryLimitError(""); // Clear the message
      }

      return updated;
    });
  };

  // Function to handle search input changes
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearch(value); // This updates the dropdown with search results
  };

  // Function to execute search when icon is clicked
  const executeSearch = () => {
    if (!searchValue.trim()) return;

    // Make API call to selectiveSearchJobs.php
    if (userId) {
      fetch(
        `${API_BASE_URL}/backend/api/JobsPage/selectiveSearchJobs.php?userId=${userId}&keyword=${encodeURIComponent(
          searchValue
        )}`
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") {
            // Check if there are any results
            if (data.jobs.length === 0) {
              // Show the no results alert
              setNoResultsAlert(true);
            } else {
              // Show success alert with the count of jobs found
              setAlertMessage(
                `Found ${data.jobs.length} job${
                  data.jobs.length > 1 ? "s" : ""
                } matching "${searchValue}"`
              );
              setSuccessAlert(true);
            }
            // Pass full filtered results to parent component to update the board
            onSearch(searchValue, true);
          } else {
            console.error("Error searching jobs:", data.message);
          }
        })
        .catch((err) => console.error("Error searching jobs:", err));
    }
  };

  // Function to close the no results alert
  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setNoResultsAlert(false);
    setSuccessAlert(false);
  };

  // Function to clear search
  const clearSearch = () => {
    setSearchValue("");
    setNoResultsAlert(false); // Clear the alert when resetting search
    setSuccessAlert(false); // Clear success alert too

    // Fetch all jobs when clearing search
    if (userId) {
      fetch(
        `${API_BASE_URL}/backend/api/JobsPage/fetchJobs.php?userId=${userId}`
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") {
            onSearch(""); // Reset search and clear dropdown
          } else {
            console.error("Error fetching jobs:", data.message);
          }
        })
        .catch((err) => console.error("Error fetching jobs:", err));
    }
  };

  return (
    <div
      style={{ backgroundColor: "#3282b8" }}
      className="kanban-board-container"
    >
      {/* Filter Categories Button */}
      <div className="kanban-header">
        <div className="kanban-controls">
          <div className="search-container-header">
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchValue}
              onChange={handleSearchChange}
              className="search-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  executeSearch();
                }
              }}
            />
            <div className="search-icons-container">
              <SearchIcon
                className="search-icon"
                style={{ cursor: "pointer" }}
                onClick={executeSearch}
              />
              {searchValue && (
                <ClearIcon
                  className="clear-icon"
                  style={{ cursor: "pointer" }}
                  onClick={clearSearch}
                />
              )}
            </div>
            {searchResults && searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((result) => (
                  <div key={result.id} onClick={() => onResultClick(result)}>
                    <p className="font-semibold">
                      {result.details.JobPosition}
                    </p>
                    <p className="text-sm text-gray-600">
                      {result.details.CompanyName}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button
            onClick={() => setIsFilterModalOpen(true)}
            variant="contained"
            color="primary"
            className="filter-button"
            style={{ backgroundColor: "#3282b8", color: "white" }}
          >
            <TuneIcon />{" "}
            <span className="filter-button-text">Filter Categories</span>
          </Button>
        </div>
      </div>

      {/* Filter Modal */}
      <Modal
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
      >
        <div className="modal-container">
          <h3>Customize Job Categories</h3>
          <p
            style={{
              color: categoryLimitError ? "red" : "#333",
              marginBottom: "10px",
            }}
          >
            {categoryLimitError || "Customize Job Categories"}
          </p>

          <div className="tag-list-scrollable">
            {/* Default Categories */}
            {defaultCategories.map((category) => (
              <label key={category.id} className="custom-tag clickable-label">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTags.includes(category.id)}
                      onChange={() => handleTagToggle(category.id)}
                    />
                  }
                  label={category.name}
                />
              </label>
            ))}

            {/* Custom Tags with inline editing */}
            {customTags.map((tag) => (
              <label key={tag.id} className="custom-tag clickable-label">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTags.includes(tag.id)}
                      onChange={() => handleTagToggle(tag.id)}
                    />
                  }
                  label={
                    editingTagId === tag.id ? (
                      <input
                        type="text"
                        value={editingTagName}
                        onChange={(e) => setEditingTagName(e.target.value)}
                        style={{ width: "70%" }}
                      />
                    ) : (
                      tag.name
                    )
                  }
                />
                {/* Only show edit and delete icons if the tag is not a default category */}
                {!defaultCategoryIds.includes(tag.id) && (
                  <>
                    {editingTagId === tag.id ? (
                      <CheckIcon
                        className="confirm-tag-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          // API call to update the tag name
                          fetch(
                            `${API_BASE_URL}/backend/api/JobsPage/update_column_name.php`,
                            {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                userId,
                                oldTag: tag.name,
                                newTag: editingTagName,
                              }),
                            }
                          )
                            .then((res) => res.json())
                            .then((data) => {
                              if (data.status === "success") {
                                // Update state: update customTags and columns with new tag name
                                setCustomTags((prevTags) =>
                                  prevTags.map((t) =>
                                    t.id === tag.id
                                      ? { ...t, name: editingTagName }
                                      : t
                                  )
                                );
                                setColumns((prevColumns) =>
                                  prevColumns.map((col) =>
                                    col.id === tag.id
                                      ? { ...col, name: editingTagName }
                                      : col
                                  )
                                );
                                setEditingTagId(null);
                                setEditingTagName("");
                                // Re-fetch tags to ensure state sync with backend
                                fetchTags();
                              } else {
                                alert("Error updating tag: " + data.message);
                              }
                            })
                            .catch((err) => {
                              console.error("Error updating tag:", err);
                            });
                        }}
                        style={{ cursor: "pointer", marginRight: "8px" }}
                      />
                    ) : (
                      <EditIcon
                        className="edit-tag-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTagId(tag.id);
                          setEditingTagName(tag.name);
                        }}
                        style={{ cursor: "pointer", marginRight: "8px" }}
                      />
                    )}
                    <CloseIcon
                      className="delete-tag-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTagToDelete(tag.id);
                        setIsDeleteModalOpen(true);
                      }}
                    />
                  </>
                )}
              </label>
            ))}
          </div>

          {/* Input Field + Button for adding new tag */}
          <div className="tag-input-container">
            <input
              type="text"
              placeholder="New tag name"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              className="tag-input"
            />
            <Button
              onClick={addCustomTag}
              variant="contained"
              color="primary"
              disabled={customTags.length >= 4}
            >
              Add Tag
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="delete-confirmation">
          <h3>Are you sure you want to delete this tag?</h3>
          <div className="confirmation-buttons">
            <Button onClick={removeCustomTag} variant="contained" color="error">
              Yes
            </Button>
            <Button
              onClick={() => setIsDeleteModalOpen(false)}
              variant="contained"
            >
              No
            </Button>
          </div>
        </div>
      </Modal>

      {/* No Results Alert - Modified for better responsiveness */}
      <Snackbar
        open={noResultsAlert}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        className="responsive-alert"
      >
        <Alert
          onClose={handleCloseAlert}
          severity="info"
          sx={{ width: "100%" }}
        >
          No jobs found matching "
          <span className="search-term">{searchValue}</span>"
        </Alert>
      </Snackbar>

      {/* Success Alert - Modified for better responsiveness */}
      <Snackbar
        open={successAlert}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        className="responsive-alert"
      >
        <Alert
          onClose={handleCloseAlert}
          severity="success"
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default KanbanBoardTwo;
